import { OverlayModule } from '@angular/cdk/overlay';
import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { EntityTypes } from '@app/models/enums/EntityTypes';
import { EntitySearchColumnDto } from '@http/clients/api-proxies';
import { ColumnVisibilityService } from '@services/column-visibility.service';
import { CheckboxComponent, IconSanitizerService, ObjectExtensionsService, SearchInputComponent } from 'ngx-lib';

export interface ColumnType extends EntitySearchColumnDto {
    isSelected?: boolean;
}

@Component({
    selector: 'app-column-visibility',
    templateUrl: './column-visibility.component.html',
    styleUrl: './column-visibility.component.scss',
    imports: [OverlayModule, SearchInputComponent, CheckboxComponent]
})
export class ColumnVisibilityComponent implements OnInit {
    entityType = input.required<EntityTypes>();

    overlayOpen = signal(false);
    searchText = signal('');
    settingsPanel?: SafeHtml;

    filteredColumns = computed(() => {
        const searchValue = this.searchText();
        const columns = this.columnVisibilityService.columnVisibilityState();

        if (!searchValue) {
            return columns;
        }

        return columns.filter((column: ColumnType) =>
            column.displayName.toLowerCase().includes(searchValue.toLowerCase())
        );
    });

    allSelected = computed(() => {
        const columns = this.columnVisibilityService.columnVisibilityState();
        return columns
            .filter((column: ColumnType) => !column.isFrozen)
            .every((column: ColumnType) => column.isSelected);
    });

    enableApplyBtn = computed(() => {
        const currentState = this.columnVisibilityService.columnVisibilityState();
        const lastState = this.columnVisibilityService.lastAppliedState();
        return !this.objectExtensionService.isEqualTo(lastState, currentState);
    });

    private readonly columnVisibilityService = inject(ColumnVisibilityService);
    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly objectExtensionService = inject(ObjectExtensionsService);

    async ngOnInit(): Promise<void> {
        this.settingsPanel = this.iconSanitizer.getIcon('settingsPanel');
        await this.columnVisibilityService.initColumnVisibilityState(this.entityType());
    }

    toggleOverlay() {
        this.overlayOpen.update((value) => !value);
    }

    onSearchTextChange(text: string) {
        this.searchText.set(text);
    }

    onAllChange(value: boolean) {
        const currentState = this.columnVisibilityService.columnVisibilityState();
        const updatedColumns = currentState.map((column: ColumnType) => ({
            ...column,
            isSelected: column.isFrozen ? column.isSelected : value
        }));
        this.columnVisibilityService.setColumnVisibilityState(updatedColumns);
    }

    onCheckboxChange() {
        const currentState = this.columnVisibilityService.columnVisibilityState();
        this.columnVisibilityService.setColumnVisibilityState([...currentState]);
    }

    apply() {
        this.columnVisibilityService.updateGridColumnVisibility(this.columnVisibilityService.columnVisibilityState());
        this.closeOverlay();
    }

    closeOverlay() {
        this.overlayOpen.set(false);
        this.searchText.set('');
    }
}
